import React, { useState } from 'react';
import { CATEGORIES, CATEGORY_COLORS, formatPrice } from '../data/components';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Search, Armchair, UtensilsCrossed, Tent, Archive,
  Monitor, ChevronDown, ChevronRight, GripVertical, Layout, Layers
} from 'lucide-react';

const ICON_MAP = {
  armchair: Armchair, 'utensils-crossed': UtensilsCrossed, tent: Tent,
  archive: Archive, monitor: Monitor, layout: Layout, layers: Layers,
};

export default function ComponentLibrary({ components, onAddComponent }) {
  const [search, setSearch] = useState('');
  const [expandedCats, setExpandedCats] = useState(new Set(['Seating', 'Utility', 'Lifestyle', 'Storage', 'Electronics', 'Body', 'Upholstery']));

  const filtered = components.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.category.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = CATEGORIES.map(cat => ({
    ...cat,
    items: filtered.filter(c => c.category === cat.id),
  }));

  const handleDragStart = (e, comp) => {
    e.dataTransfer.setData('application/json', JSON.stringify(comp));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleClick = (e, comp) => {
    // Double-click to add component
    if (onAddComponent) {
      onAddComponent(comp.comp_id);
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="component-library">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <h2 className="font-chivo font-semibold text-sm text-white mb-3">Components</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <Input
            data-testid="component-search"
            placeholder="Search components..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-8 text-xs bg-slate-800/50 border-white/5 text-white placeholder:text-slate-600"
          />
        </div>
      </div>

      {/* Category List */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-1">
          {grouped.map(cat => {
            const CatIcon = ICON_MAP[cat.icon] || Archive;
            const colors = CATEGORY_COLORS[cat.id] || CATEGORY_COLORS.Storage;
            const isExpanded = expandedCats.has(cat.id);

            return (
              <div key={cat.id} data-testid={`category-${cat.id.toLowerCase()}`}>
                {/* Category Header */}
                <button
                  data-testid={`category-toggle-${cat.id.toLowerCase()}`}
                  onClick={() => {
                    setExpandedCats(prev => {
                      const next = new Set(prev);
                      if (next.has(cat.id)) next.delete(cat.id);
                      else next.add(cat.id);
                      return next;
                    });
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-800/50 transition-colors group"
                >
                  <CatIcon className={`w-4 h-4 ${colors.text}`} />
                  <span className="text-xs font-medium text-slate-300 flex-1 text-left">{cat.label}</span>
                  <Badge variant="secondary" className="text-[10px] h-5 bg-slate-800 text-slate-400">{cat.items.length}</Badge>
                  {isExpanded ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
                </button>

                {/* Component Items */}
                {isExpanded && (
                  <div className="ml-2 mt-1 space-y-1">
                    {cat.items.map(comp => (
                      <div
                        key={comp.comp_id}
                        data-testid={`draggable-${comp.comp_id}`}
                        draggable
                        onDragStart={e => handleDragStart(e, comp)}
                        onDoubleClick={e => handleClick(e, comp)}
                        className={`group/item flex items-start gap-2 p-2 rounded-lg ${colors.bg} border ${colors.border} cursor-grab active:cursor-grabbing hover:-translate-y-0.5 transition-all duration-200 hover:shadow-lg hover:ring-1 hover:ring-blue-500/50`}
                        title="Double-click to add or drag to canvas"
                      >
                        {/* Product thumbnail */}
                        {comp.image ? (
                          <div className="w-12 h-12 rounded-md bg-slate-800/50 border border-white/5 shrink-0 overflow-hidden flex items-center justify-center">
                            <img src={comp.image} alt="" className="w-full h-full object-contain p-0.5" draggable={false} />
                          </div>
                        ) : (
                          <div className={`w-12 h-12 rounded-md ${colors.bg} border ${colors.border} shrink-0 flex items-center justify-center`}>
                            <span className={`text-[9px] font-bold ${colors.text}`}>{comp.grid_w}x{comp.grid_h}</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-medium text-slate-200 truncate block">{comp.name}</span>
                          <p className="text-[10px] text-slate-500 leading-relaxed line-clamp-1">{comp.description}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="font-jetbrains text-[10px] text-blue-400">{formatPrice(comp.price)}</span>
                            <span className="font-jetbrains text-[10px] text-slate-600">{comp.weight}kg</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Hint */}
      <div className="p-3 border-t border-white/5">
        <p className="text-[10px] text-slate-600 text-center">Double-click to add • Drag to position</p>
      </div>
    </div>
  );
}
